import { forwardRef } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "subtle"
  | "danger";
export type ButtonSize = "sm" | "md" | "icon" | "icon-sm";

const BASE =
  "inline-flex select-none items-center justify-center gap-1.5 rounded-lg font-medium transition-[background-color,border-color,color,opacity] disabled:cursor-not-allowed disabled:opacity-50";

const VARIANTS: Record<ButtonVariant, string> = {
  // `bg-ink text-paper` inverts automatically: black on white in light mode,
  // white on black in dark mode.
  primary: "bg-ink text-paper hover:opacity-90",
  secondary: "border border-line bg-paper text-ink hover:bg-shell",
  ghost: "text-ink-soft hover:bg-shell hover:text-ink",
  subtle: "bg-shell text-ink-soft hover:bg-line/70 hover:text-ink",
  danger: "bg-danger text-white hover:opacity-90",
};

const SIZES: Record<ButtonSize, string> = {
  sm: "h-8 px-2.5 text-[13px]",
  md: "h-9 px-3.5 text-sm",
  icon: "h-9 w-9",
  "icon-sm": "h-8 w-8",
};

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = "secondary",
      size = "md",
      loading = false,
      className,
      children,
      disabled,
      type = "button",
      ...rest
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        className={cn(BASE, VARIANTS[variant], SIZES[size], className)}
        {...rest}
      >
        {loading ? <Loader2 size={14} className="animate-spin" /> : null}
        {children}
      </button>
    );
  },
);
